// client/src/components/billingDetails/BillingDetailList.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import billingDetailService from '../../services/billingDetailService';

const BillingDetailList = () => {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const [billingDetails, setBillingDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailToDelete, setDetailToDelete] = useState(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  // Current language
  const lang = i18n.language;

  // Function to load billing details
  const loadBillingDetails = async () => {
    try {
      setLoading(true);
      const response = await billingDetailService.getBillingDetails();
      setBillingDetails(response.data);
      setError('');
    } catch (err) {
      setError(t('errorLoadingBillingDetails'));
    } finally {
      setLoading(false);
    }
  };

  // Load billing details on component mount
  useEffect(() => {
    loadBillingDetails();
  }, []);

  // Handle billing detail deletion
  const handleDeleteClick = (detail) => {
    setDetailToDelete(detail);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await billingDetailService.deleteBillingDetail(detailToDelete.id);
      setSuccess(t('billingDetailDeletedSuccess'));
      loadBillingDetails();
    } catch (err) {
      setError(t('errorDeletingBillingDetail'));
    } finally {
      setDeleteDialogOpen(false);
      setDetailToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDetailToDelete(null);
  };

  // Handle info dialog
  const handleInfoClick = (detail) => {
    setSelectedDetail(detail);
    setInfoDialogOpen(true);
  };

  const handleCloseInfo = () => {
    setInfoDialogOpen(false);
    setSelectedDetail(null);
  };

  const handleCloseAlert = () => {
    setSuccess('');
    setError('');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('billingDetails')}
      </Typography>

      {/* Alert messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Add Billing Detail button */}
      <Box sx={{ mb: 2, textAlign: direction === 'ltr' ? 'right' : 'left' }}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/billing-details/create"
        >
          {t('addBillingDetail')}
        </Button>
      </Box>

      {/* Billing Details Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : billingDetails.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('noBillingDetailsFound')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('type')}</TableCell>
                <TableCell>{t('faculty')}</TableCell>
                <TableCell>{t('createdBy')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {billingDetails.map((detail) => (
                <TableRow key={detail.id}>
                  <TableCell>{detail.name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={t(detail.detail_type)} 
                      color={detail.detail_type === 'university' ? 'primary' : 'secondary'} 
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {detail.faculty 
                      ? detail.faculty[`name_${lang}`] 
                      : t('notApplicable')}
                  </TableCell>
                  <TableCell>{detail.creator?.username || ''}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="info"
                      onClick={() => handleInfoClick(detail)}
                    >
                      <InfoIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      component={RouterLink}
                      to={`/billing-details/edit/${detail.id}`}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(detail)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deleteBillingDetailConfirmation', { 
              name: detailToDelete?.name 
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={handleCloseInfo}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedDetail?.name || ''}</DialogTitle>
        <DialogContent>
          {selectedDetail && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {t('type')}: 
                <Chip 
                  label={t(selectedDetail.detail_type)} 
                  color={selectedDetail.detail_type === 'university' ? 'primary' : 'secondary'} 
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
              
              {selectedDetail.faculty && (
                <Typography variant="subtitle1" gutterBottom>
                  {t('faculty')}: {selectedDetail.faculty[`name_${lang}`]}
                </Typography>
              )}
              
              <Typography variant="subtitle1" gutterBottom>
                {t('createdBy')}: {selectedDetail.creator?.username || ''}
                {selectedDetail.created_at && 
                  ` • ${new Date(selectedDetail.created_at).toLocaleDateString()}`}
              </Typography>
              
              {selectedDetail.description && (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('description')}:
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedDetail.description}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInfo}>{t('close')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BillingDetailList;